import { defineStore } from 'pinia';
import { ref } from 'vue';
import NProgress from 'nprogress';

import { authApi } from '@/utilities/axios';
import EventBus from '@/utilities/event-bus';
import { useAuthStore } from '@/modules/auth/store';
import { showToast } from '@/utilities/toast-handler';
import { userCompanyPreference } from '@/composables/CompanyPreferenceComposable';

const API = process.env.VUE_APP_API_VERSION + '/companies';

export const useLabelRenameStore = defineStore('labelRename', () => {
    const authStore = useAuthStore();

    // { "Task": "Debrief", "Report": "Summary", ... } — direct from API's label_overrides.
    const overrides = ref({});
    // Rows for the settings page edit form.
    const renameLabels = ref([]);
    const loading = ref(false);

    // Cached promise so ensureLoaded() only fetches once per session.
    let loadPromise = null;

    const companyId = () => authStore.getLoggedUserCompanyId;

    // Pass the default name, get the renamed version. If not overridden, returns the fallback (or the default name itself).
    const getLabel = (defaultName, fallback = defaultName) => {
        return overrides.value[defaultName] ?? fallback ?? defaultName;
    };

    const fetchRenameLabels = async () => {
        try {
            loading.value = true;
            const { data } = await authApi.get(`${API}/${companyId()}/label-overrides`);
            overrides.value = data?.label_overrides ?? {};
            renameLabels.value = (data?.rename_labels ?? []).map((item) => {
                const current = item.current_value ?? item.default_name;
                return {
                    ...item,
                    original_value: current,
                    selected_value: current,
                };
            });
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch label rename preferences');
        } finally {
            loading.value = false;
        }
    };

    // Check the company preference, load overrides if enabled. Safe to call from anywhere — runs only once.
    // Pass prefs if you already have them (e.g. menu setup) to avoid a duplicate preference fetch.
    const ensureLoaded = (prefs = null) => {
        if (loadPromise) return loadPromise;
        const id = companyId();
        if (!id) return (loadPromise = Promise.resolve());
        const prefsPromise = prefs
            ? Promise.resolve(prefs)
            : userCompanyPreference().fetchCompanyPreference(id);
        loadPromise = prefsPromise
            .then((p) => {
                if (p?.label_rename_enabled === 1) return fetchRenameLabels();
            })
            .catch(() => {});
        return loadPromise;
    };

    const saveRenameLabels = async () => {
        try {
            NProgress.start();
            EventBus.emit('show-operation-loader');

            const changed = renameLabels.value.filter((item) => {
                const original = item.original_value ?? item.current_value ?? item.default_name;
                return item.selected_value !== original;
            });

            if (changed.length === 0) {
                showToast({}, 'No label changes to save');
                return false;
            }

            let response;
            for (const item of changed) {
                const key = item.default_name ?? item.code;
                response = await authApi.post(`${API}/${companyId()}/label-overrides`, {
                    key,
                    display_name: item.selected_value,
                });
                overrides.value[key] = item.selected_value;
                item.current_value = item.selected_value;
                item.original_value = item.selected_value;
            }
            showToast(response?.toast, 'Labels have been updated successfully');
            // Menu builds label strings only at initial setup, so reload to refresh everything.
            setTimeout(() => window.location.reload(), 500);
            return true;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to save label rename preferences');
        } finally {
            NProgress.done();
            EventBus.emit('hide-operation-loader');
        }
    };

    return {
        overrides,
        renameLabels,
        loading,
        getLabel,
        ensureLoaded,
        fetchRenameLabels,
        saveRenameLabels,
    };
});
